import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  List,
  ListItem,
  ListItemButton,
  Paper,
} from '@mui/material';

interface PlaceDetails {
  name: string;
  lat: number;
  lng: number;
}

interface Props {
  onPlaceSelect: (place: PlaceDetails) => void;
  label?: string;
  placeholder?: string;
  value?: any;
}

const AutoCompletePlaces: React.FC<Props> = ({
  onPlaceSelect,
  label = 'Location',
  placeholder = 'Enter a location',
  value,
}) => {
  const [input, setInput] = useState(value?.name || '');
  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [showPredictions, setShowPredictions] = useState(false);
  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!autocompleteService.current && window.google) {
      autocompleteService.current = new google.maps.places.AutocompleteService();
    }
    if (!placesService.current && window.google && mapRef.current) {
      const map = new window.google.maps.Map(mapRef.current);
      placesService.current = new google.maps.places.PlacesService(map);
    }
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInput(value);
    if (value && autocompleteService.current) {
      autocompleteService.current.getPlacePredictions(
        { input: value },
        (predictions, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
            setPredictions(predictions);
            setShowPredictions(true);
          } else {
            setPredictions([]);
            setShowPredictions(false);
          }
        }
      );
    } else {
      setPredictions([]);
      setShowPredictions(false);
    }
  };

  const handlePredictionSelect = (prediction: google.maps.places.AutocompletePrediction) => {
    if (placesService.current) {
      placesService.current.getDetails(
        { placeId: prediction.place_id },
        (place, status) => {
          if (
            status === google.maps.places.PlacesServiceStatus.OK &&
            place &&
            place.geometry?.location
          ) {
            const placeDetails: PlaceDetails = {
              name: place.name || prediction.description,
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
            };
            setInput(placeDetails.name);
            setShowPredictions(false);
            setPredictions([]);
            onPlaceSelect(placeDetails);
          }
        }
      );
    }
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <div ref={mapRef} style={{ display: 'none' }} />
      <TextField
        fullWidth
        label={label}
        value={input}
        onChange={handleInputChange}
        placeholder={placeholder}
      />
      {showPredictions && predictions.length > 0 && (
        <Paper
          style={{
            position: 'absolute',
            width: '100%',
            zIndex: 1000,
            marginTop: '8px',
          }}
        >
          <List>
            {predictions.map((prediction) => (
              <ListItem key={prediction.place_id} disablePadding>
                <ListItemButton onClick={() => handlePredictionSelect(prediction)}>
                  <div>
                    <div>{prediction.structured_formatting.main_text}</div>
                    <div style={{ fontSize: '0.8em', color: '#666' }}>
                      {prediction.structured_formatting.secondary_text}
                    </div>
                  </div>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </div>
  );
};

export default AutoCompletePlaces;
